import { InputBase } from '@mui/material';
import { useState } from 'react';
import { connect, useDispatch } from 'react-redux';
import { Dispatch } from 'redux';
import { updateGeoChartStyleOptions } from '../../../redux/ChartPoperties/ChartControlsActions';
import { ChartOptionsProps, ChartOptionsStateProps } from '../CommonInterfaceForChartOptions';
import { ColorSchemes } from './ColorScheme';
import ChartColors from './ChartColors';
import SwitchWithInput from '../SwitchWithInput';
import { ChartConGeoChartControls } from '../../../redux/ChartPoperties/ChartControlsInterface';
import { OrientationRadioGroup } from '../Legend/LegendComponents/OrientationRadioGroup';
import ColorPickerPopover from './ChartColorComponents/ColorPickerPopover';

interface GeoChartAction {
  updateGeoChartStyleOptions: (propKey: string, option: string, value: any) => void;
}

const inputBaseStyle = {
  border: '2px solid rgba(224,224,224,1)',
  borderRadius: '3px',
  height: '20px',
  fontSize: '12px',
  padding: '0px 4px',
  color: '#a7a7a7',
};

const FilledMapColors = ({
  chartControls,
  tabTileProps,
  updateGeoChartStyleOptions,
  iconColor,
}: ChartOptionsProps & GeoChartAction & { iconColor?: string }) => {
  const propKey = `${tabTileProps.selectedTabId}.${tabTileProps.selectedTileId}`;

  const dispatch = useDispatch();

  const geoStyle: ChartConGeoChartControls =
    chartControls.properties[propKey].geoChartControls || {};

  const selectedScheme = ColorSchemes.find(
    (scheme) => scheme.name === chartControls.properties[propKey].colorScheme,
  );

  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [selectedColor, setSelectedColor] = useState<string>('');
  const [colorFieldName, setColorFieldName] = useState<string>('');

  const minVal = Number(geoStyle.minValue);
  const maxVal = Number(geoStyle.maxValue);
  const minMaxError =
    geoStyle.minValue !== '' &&
    geoStyle.maxValue !== '' &&
    !isNaN(minVal) &&
    !isNaN(maxVal) &&
    minVal >= maxVal;

  const renderSmallColorBox = (currentColor: string, fieldName: string) => (
    <div
      style={{
        height: '1rem',
        width: '30px',
        minWidth: '30px',
        backgroundColor: currentColor,
        border: '1px solid darkgray',
        cursor: 'pointer',
        borderRadius: '2px',
        marginRight: '10px',
      }}
      onClick={(e) => {
        e.stopPropagation();
        setSelectedColor(currentColor);
        setColorFieldName(fieldName);
        setAnchorEl(e.currentTarget);
      }}
    />
  );

  return (
    <div style={{ marginTop: '20px', paddingTop: '8px', paddingLeft: '4px', overflowX: 'hidden' }}>
      <OrientationRadioGroup
        orientation={geoStyle.manualColor ? 'manual' : 'auto'}
        options={[
          { name: 'Auto', key: 'auto' },
          { name: 'Manual', key: 'manual' },
        ]}
        onChange={(key) => {
          updateGeoChartStyleOptions(propKey, 'manualColor', key === 'manual');
        }}
        style={{ width: '94%', marginBottom: '10px' }}
      />

      {!geoStyle.manualColor ? (
        <div>
          <div style={{ marginTop: '-25px' }}>
            <ChartColors iconColor={iconColor} />
          </div>

          <div
            className="optionDescription"
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              paddingLeft: '1px',
              marginTop: '10px',
            }}
          >
            <label
              htmlFor="divergentMode"
              className="enableDisableLabel"
              style={{
                marginRight: '10px',
                fontWeight: 600,
                color: iconColor ? iconColor : '#404040',
              }}
            >
              Use Divergent Colors
            </label>
            <div
              style={{
                width: '50px',
                height: '24px',
                display: 'flex',
                transform: 'translateX(60px)',
                marginBottom: '7px',
              }}
            >
              <SwitchWithInput
                isChecked={!!geoStyle.isDivergentModeEnabled}
                onSwitch={() =>
                  updateGeoChartStyleOptions(
                    propKey,
                    'isDivergentModeEnabled',
                    !geoStyle.isDivergentModeEnabled,
                  )
                }
              />
            </div>
          </div>
        </div>
      ) : (
        <div>
          <div
            className="optionDescription"
            style={{
              fontSize: '12px',
              marginTop: '-5px',
              color: iconColor ? iconColor : '#404040',
            }}
          >
            Min
          </div>
          <div
            className="optionDescription"
            style={{
              marginTop: '0px',
              paddingTop: '0',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-start',
            }}
          >
            {renderSmallColorBox(geoStyle.minColor, 'minColor')}
            <InputBase
              style={{ height: '1.25rem', width: '50%' }}
              value={geoStyle.minValue}
              onChange={(e: any) => {
                updateGeoChartStyleOptions(propKey, 'minValue', e.target.value);
              }}
              sx={inputBaseStyle}
              placeholder={'Enter Min Value'}
            />
          </div>

          <div
            className="optionDescription"
            style={{
              fontSize: '12px',
              marginTop: '-5px',
              color: iconColor ? iconColor : '#404040',
            }}
          >
            Max
          </div>
          <div
            className="optionDescription"
            style={{
              marginTop: '0px',
              paddingTop: '0',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-start',
            }}
          >
            {renderSmallColorBox(geoStyle.maxColor, 'maxColor')}
            <InputBase
              style={{ height: '1.25rem', width: '50%' }}
              value={geoStyle.maxValue}
              onChange={(e: any) => {
                updateGeoChartStyleOptions(propKey, 'maxValue', e.target.value);
              }}
              sx={inputBaseStyle}
              placeholder={'Enter Max Value'}
            />
          </div>

          {minMaxError && (
            <div
              style={{
                color: '#d32f2f',
                fontSize: '12px',
                marginTop: '5px',
                marginBottom: '5px',
                textAlign: 'left',
                paddingLeft: '10px',
              }}
            >
              Max value should be greater than Min Value
            </div>
          )}

          <div
            className="optionDescription"
            style={{
              marginTop: '10px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-start',
            }}
          >
            {renderSmallColorBox(geoStyle.borderColor, 'borderColor')}
            <div style={{ fontSize: '12px', color: iconColor ? iconColor : '#404040' }}>
              Border Color
            </div>
          </div>

          <div
            className="optionDescription"
            style={{
              marginTop: '10px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-start',
            }}
          >
            {renderSmallColorBox(geoStyle.areaColor, 'areaColor')}
            <div style={{ fontSize: '12px', color: iconColor ? iconColor : '#404040' }}>
              Area Color
            </div>
          </div>

          <div
            className="optionDescription"
            style={{ fontWeight: 600, marginTop: '10px', color: iconColor ? iconColor : '#404040' }}
          >
            EMPHASIS ON MOUSEOVER:
          </div>
          <div
            className="optionDescription"
            style={{
              marginTop: '5px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-start',
              marginBottom: '15px',
            }}
          >
            {renderSmallColorBox(geoStyle.emphasisAreaColor, 'emphasisAreaColor')}
            <div style={{ fontSize: '12px', color: iconColor ? iconColor : '#404040' }}>
              Area Color
            </div>
          </div>

          <ColorPickerPopover
            isOpen={Boolean(anchorEl)}
            anchorEl={anchorEl}
            onClose={() => setAnchorEl(null)}
            currentColor={selectedColor}
            onColorChange={(color: any) => {
              setSelectedColor(color);
              updateGeoChartStyleOptions(propKey, colorFieldName, color);
            }}
            propKey={propKey}
            selectedMenu=""
          />
        </div>
      )}
    </div>
  );
};

const mapStateToProps = (state: ChartOptionsStateProps, ownProps: any) => {
  return {
    chartControls: state.chartControls,
    tabTileProps: state.tabTileProps,
    chartProperties: state.chartProperties,
  };
};

const mapDispatchToProps = (dispatch: Dispatch<any>) => {
  return {
    updateGeoChartStyleOptions: (propKey: string, option: string, value: any) =>
      dispatch(updateGeoChartStyleOptions(propKey, option, value)),
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(FilledMapColors);
